import { type ReactNode, useEffect, useRef } from "react";
import { I18nextProvider } from "react-i18next";

import { createDocsI18n } from "@mountayaapp/locales";

/**
 * DocsLocaleProviderProps carry the already-resolved active language and the
 * subtree that consumes translations.
 */
interface DocsLocaleProviderProps {
  children: ReactNode;
  language: string;
}

/**
 * DocsLocaleProvider binds a single i18next instance to the docs tree. Unlike
 * the web app, there is no identity store or session here: the active language
 * is resolved from the URL by the caller and passed in, so the instance is built
 * once (useRef) already localized and the first paint matches the prerendered
 * markup. A later language change is synced by an effect calling changeLanguage —
 * syncing an external mutable store is a legitimate effect, not derived state.
 * Only English ships today; the language prop is the seam a second language flows
 * through later.
 */
export const DocsLocaleProvider = ({ children, language }: DocsLocaleProviderProps) => {
  const instanceRef = useRef<ReturnType<typeof createDocsI18n> | null>(null);
  if (instanceRef.current === null) {
    instanceRef.current = createDocsI18n(language);
  }

  const instance = instanceRef.current;

  useEffect(() => {
    if (instance.language !== language) {
      void instance.changeLanguage(language);
    }
  }, [instance, language]);

  return <I18nextProvider i18n={instance}>{children}</I18nextProvider>;
};
